import { useEffect, useState, createContext, useContext, useCallback, useMemo } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useSearchParams, useParams, Navigate } from "react-router-dom";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Constants
const MS_PER_DAY = 86400000;
const MS_PER_HOUR = 3600000;
const MS_PER_MINUTE = 60000;
const MS_PER_SECOND = 1000;
const REFRESH_INTERVAL = 30000;
const HTTP_UNAUTHORIZED = 401;

axios.defaults.withCredentials = true;

// Auth Context
const AuthContext = createContext(null);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (err) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    setUser(response.data);
    return response.data;
  };

  const register = async (name, email, password) => {
    const response = await axios.post(`${API}/auth/register`, { name, email, password });
    return response.data;
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`);
    setUser(false);
  };

  const refreshUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (err) {
      setUser(false);
    }
  };

  const contextValue = useMemo(() => ({ 
    user, setUser, loading, login, register, logout, refreshUser 
  }), [user, loading]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => useContext(AuthContext);

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-[#FBF9F6] flex items-center justify-center"><p className="font-mono text-[#6B5B4F]">Wird geladen...</p></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const formatApiError = (err) => {
  const detail = err.response?.data?.detail;
  if (!detail) return "Ein Fehler ist aufgetreten";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e.msg || JSON.stringify(e)).join(" ");
  return String(detail);
};

// Countdown
const Countdown = () => {
  const targetDate = new Date('2026-05-31T00:00:00');
  
  const calculateTimeLeft = useCallback(() => {
    const diff = targetDate - new Date();
    if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    return { 
      days: Math.floor(diff / MS_PER_DAY), 
      hours: Math.floor((diff / MS_PER_HOUR) % 24), 
      minutes: Math.floor((diff / MS_PER_MINUTE) % 60), 
      seconds: Math.floor((diff / MS_PER_SECOND) % 60) 
    };
  }, []);

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());
  
  useEffect(() => { 
    const timer = setInterval(() => setTimeLeft(calculateTimeLeft()), MS_PER_SECOND); 
    return () => clearInterval(timer); 
  }, [calculateTimeLeft]);
  
  const fmt = (n) => String(n).padStart(2, '0');
  const timeItems = [
    { key: 'days', v: timeLeft.days, l: 'TAGE' }, 
    { key: 'hours', v: timeLeft.hours, l: 'STD' }, 
    { key: 'minutes', v: timeLeft.minutes, l: 'MIN' }, 
    { key: 'seconds', v: timeLeft.seconds, l: 'SEK' }
  ];
  
  return (
    <div className="flex gap-3 md:gap-4" data-testid="countdown-timer">
      {timeItems.map((item) => (
        <div key={item.key} className="countdown-box text-center">
          <div className="font-mono text-2xl md:text-4xl font-bold text-[#8B7355]">{fmt(item.v)}</div>
          <div className="font-mono text-[10px] md:text-xs text-[#6B5B4F] mt-1 tracking-wider">{item.l}</div>
        </div>
      ))}
    </div>
  );
};

// Landing Page
const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    axios.post(`${API}/track-visit`).catch(() => {});
  }, []);

  return (
    <div className="hero-section">
      <div className="question-mark">?</div>
      <div className="form-container text-center">
        <div className="mb-12 opacity-0 animate-fadeInUp stagger-1">
          <p className="font-mono text-xs tracking-[0.3em] text-[#6B5B4F] mb-4">COMING SOON</p>
          <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-black uppercase tracking-tighter leading-none text-[#2D2016]" data-testid="main-headline">Was kommt<br />hierhin?</h1>
          <p className="font-mono text-sm md:text-base text-[#6B5B4F] mt-6 tracking-wide">Rate richtig und gewinne!</p>
        </div>
        <div className="flex justify-center mb-12 opacity-0 animate-fadeInUp stagger-2"><Countdown /></div>
        <div className="space-y-4 opacity-0 animate-fadeInUp stagger-3">
          {user ? (
            <button onClick={() => navigate('/guess')} className="mystery-button w-full" data-testid="start-guessing-button">
              {user.has_participated ? 'Meinen Status ansehen' : 'Jetzt raten'}
            </button>
          ) : (
            <>
              <button onClick={() => navigate('/register')} className="mystery-button w-full" data-testid="register-button">Registrieren & Raten</button>
              <button onClick={() => navigate('/login')} className="w-full font-mono text-xs text-[#6B5B4F] hover:text-[#8B7355] transition-colors py-3" data-testid="login-link">Bereits registriert? Anmelden</button>
            </>
          )}
        </div>
        <p className="font-mono text-[10px] text-[#6B5B4F]/50 text-center mt-8 tracking-wider opacity-0 animate-fadeInUp stagger-4">50 GEWINNER • BUY ONE GET ONE FREE</p>
      </div>
    </div>
  );
};

// Register Page
const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [formData, setFormData] = useState({ name: '', email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await register(formData.name, formData.email, formData.password);
      setSuccess(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="hero-section">
        <div className="form-container text-center">
          <div className="glass-card p-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#8B7355]/10 flex items-center justify-center">
              <svg className="w-8 h-8 text-[#8B7355]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h2 className="font-heading text-2xl font-bold text-[#2D2016] mb-4">Bestätige deine E-Mail</h2>
            <p className="font-mono text-sm text-[#6B5B4F] mb-6">
              Wir haben dir eine E-Mail an<br /><strong>{formData.email}</strong><br />gesendet. Klicke auf den Link, um fortzufahren.
            </p>
            <button onClick={() => navigate('/login')} className="font-mono text-xs text-[#8B7355] hover:underline">
              Zur Anmeldung →
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="hero-section">
      <div className="form-container">
        <div className="text-center mb-8 opacity-0 animate-fadeInUp stagger-1">
          <p className="font-mono text-xs tracking-[0.3em] text-[#6B5B4F] mb-4">MITMACHEN</p>
          <h1 className="font-heading text-4xl md:text-5xl font-black uppercase tracking-tighter text-[#2D2016]">Registrieren</h1>
        </div>
        <form onSubmit={handleSubmit} className="space-y-6 opacity-0 animate-fadeInUp stagger-2" data-testid="register-form">
          <div>
            <label className="font-mono text-xs text-[#6B5B4F] uppercase tracking-widest mb-2 block">Name</label>
            <input type="text" required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="mystery-input w-full" placeholder="Dein Name" data-testid="register-name" />
          </div>
          <div>
            <label className="font-mono text-xs text-[#6B5B4F] uppercase tracking-widest mb-2 block">E-Mail</label>
            <input type="email" required value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="mystery-input w-full" placeholder="deine@email.de" data-testid="register-email" />
          </div>
          <div>
            <label className="font-mono text-xs text-[#6B5B4F] uppercase tracking-widest mb-2 block">Passwort</label>
            <input type="password" required minLength={6} value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} className="mystery-input w-full" placeholder="Mindestens 6 Zeichen" data-testid="register-password" />
          </div>
          {error && <div className="bg-[#C53030]/10 border border-[#C53030] p-4 text-center"><p className="text-[#C53030] font-mono text-sm font-bold">{error}</p></div>}
          <p className="font-mono text-[10px] text-[#6B5B4F]/60 text-center" data-testid="privacy-notice">Deine Daten werden nicht an Dritte weitergegeben und ausschließlich für dieses Gewinnspiel verwendet.</p>
          <button type="submit" disabled={loading} className="mystery-button w-full" data-testid="register-submit">{loading ? 'Wird registriert...' : 'Registrieren'}</button>
        </form>
        <p className="text-center mt-6 opacity-0 animate-fadeInUp stagger-3">
          <button onClick={() => navigate('/login')} className="font-mono text-xs text-[#6B5B4F] hover:text-[#8B7355] transition-colors">Bereits registriert? Anmelden</button>
        </p>
      </div>
    </div>
  );
};

// Verify Email Page
const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const token = searchParams.get('token');

  const verifyEmail = useCallback(async () => {
    try {
      const response = await axios.post(`${API}/auth/verify-email`, { token });
      setUser(response.data);
      setStatus('success');
      setTimeout(() => navigate('/guess'), 2000);
    } catch (err) {
      setStatus('error');
      setError(formatApiError(err));
    }
  }, [token, setUser, navigate]);

  useEffect(() => {
    if (!token) { setStatus('error'); setError('Kein Token gefunden'); return; }
    verifyEmail();
  }, [token, verifyEmail]);

  return (
    <div className="hero-section">
      <div className="form-container text-center">
        <div className="glass-card p-8">
          {status === 'loading' && (
            <>
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#8B7355]/10 flex items-center justify-center animate-pulse">
                <svg className="w-8 h-8 text-[#8B7355]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
              </div>
              <p className="font-mono text-sm text-[#6B5B4F]">E-Mail wird bestätigt...</p>
            </>
          )}
          {status === 'success' && (
            <>
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#2F855A]/10 flex items-center justify-center">
                <svg className="w-8 h-8 text-[#2F855A]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
              </div>
              <h2 className="font-heading text-2xl font-bold text-[#2F855A] mb-2">Bestätigt!</h2>
              <p className="font-mono text-sm text-[#6B5B4F]">Du wirst weitergeleitet...</p>
            </>
          )}
          {status === 'error' && (
            <>
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#C53030]/10 flex items-center justify-center">
                <svg className="w-8 h-8 text-[#C53030]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </div>
              <h2 className="font-heading text-2xl font-bold text-[#C53030] mb-2">Fehler</h2>
              <p className="font-mono text-sm text-[#6B5B4F] mb-4">{error}</p>
              <button onClick={() => navigate('/login')} className="mystery-button">Zur Anmeldung</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Login Page
const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showResend, setShowResend] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setShowResend(false);
    setLoading(true);
    try {
      await login(formData.email, formData.password);
      navigate('/guess');
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      if (msg.includes('E-Mail-Adresse')) setShowResend(true);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, { email: formData.email });
      setResendSuccess(true);
    } catch (err) {
      setError(formatApiError(err));
    }
    setResendLoading(false);
  };

  return (
    <div className="hero-section">
      <div className="form-container">
        <div className="text-center mb-8 opacity-0 animate-fadeInUp stagger-1">
          <p className="font-mono text-xs tracking-[0.3em] text-[#6B5B4F] mb-4">WILLKOMMEN</p>
          <h1 className="font-heading text-4xl md:text-5xl font-black uppercase tracking-tighter text-[#2D2016]">Anmelden</h1>
        </div>
        <form onSubmit={handleSubmit} className="space-y-6 opacity-0 animate-fadeInUp stagger-2" data-testid="login-form">
          <div>
            <label className="font-mono text-xs text-[#6B5B4F] uppercase tracking-widest mb-2 block">E-Mail</label>
            <input type="email" required value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="mystery-input w-full" placeholder="deine@email.de" data-testid="login-email" />
          </div>
          <div>
            <label className="font-mono text-xs text-[#6B5B4F] uppercase tracking-widest mb-2 block">Passwort</label>
            <input type="password" required value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} className="mystery-input w-full" placeholder="Dein Passwort" data-testid="login-password" />
          </div>
          {error && (
            <div className="bg-[#C53030]/10 border border-[#C53030] p-4 text-center">
              <p className="text-[#C53030] font-mono text-sm font-bold">{error}</p>
              {showResend && !resendSuccess && (
                <button type="button" onClick={handleResend} disabled={resendLoading} className="mt-2 font-mono text-xs text-[#8B7355] hover:underline">
                  {resendLoading ? 'Wird gesendet...' : 'Bestätigungslink erneut senden'}
                </button>
              )}
              {resendSuccess && <p className="mt-2 font-mono text-xs text-[#2F855A]">Neuer Link gesendet!</p>}
            </div>
          )}
          <button type="submit" disabled={loading} className="mystery-button w-full" data-testid="login-submit">{loading ? 'Wird angemeldet...' : 'Anmelden'}</button>
        </form>
        <div className="text-center mt-6 space-y-3 opacity-0 animate-fadeInUp stagger-3">
          <button onClick={() => navigate('/forgot-password')} className="font-mono text-xs text-[#8B7355] hover:underline transition-colors block mx-auto" data-testid="forgot-password-link">Passwort vergessen?</button>
          <button onClick={() => navigate('/register')} className="font-mono text-xs text-[#6B5B4F] hover:text-[#8B7355] transition-colors block mx-auto">Noch kein Konto? Registrieren</button>
        </div>
      </div>
    </div>
  );
};

// Forgot Password Page
const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSuccess(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="hero-section">
        <div className="form-container text-center">
          <div className="glass-card p-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#8B7355]/10 flex items-center justify-center">
              <svg className="w-8 h-8 text-[#8B7355]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h2 className="font-heading text-2xl font-bold text-[#2D2016] mb-4">E-Mail gesendet</h2>
            <p className="font-mono text-sm text-[#6B5B4F] mb-2">Falls ein Konto mit dieser E-Mail existiert, haben wir dir einen Link zum Zurücksetzen gesendet.</p>
            <p className="font-mono text-[10px] text-[#6B5B4F]/60 mb-6">Bitte schaue auch in deinem Spam-Ordner nach!</p>
            <button onClick={() => navigate('/login')} className="font-mono text-xs text-[#8B7355] hover:underline" data-testid="back-to-login">Zurück zur Anmeldung</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="hero-section">
      <div className="form-container">
        <div className="text-center mb-8 opacity-0 animate-fadeInUp stagger-1">
          <p className="font-mono text-xs tracking-[0.3em] text-[#6B5B4F] mb-4">PASSWORT VERGESSEN</p>
          <h1 className="font-heading text-4xl md:text-5xl font-black uppercase tracking-tighter text-[#2D2016]">Zurücksetzen</h1>
        </div>
        <form onSubmit={handleSubmit} className="space-y-6 opacity-0 animate-fadeInUp stagger-2" data-testid="forgot-password-form">
          <div>
            <label className="font-mono text-xs text-[#6B5B4F] uppercase tracking-widest mb-2 block">E-Mail</label>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mystery-input w-full" placeholder="deine@email.de" data-testid="forgot-email" />
          </div>
          {error && <div className="bg-[#C53030]/10 border border-[#C53030] p-4 text-center"><p className="text-[#C53030] font-mono text-sm font-bold">{error}</p></div>}
          <button type="submit" disabled={loading} className="mystery-button w-full" data-testid="forgot-submit">{loading ? 'Wird gesendet...' : 'Link senden'}</button>
        </form>
        <p className="text-center mt-6 opacity-0 animate-fadeInUp stagger-3">
          <button onClick={() => navigate('/login')} className="font-mono text-xs text-[#6B5B4F] hover:text-[#8B7355] transition-colors">Zurück zur Anmeldung</button>
        </p>
      </div>
    </div>
  );
};

// Reset Password Page
const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const token = searchParams.get('token');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) {
      setError('Die Passwörter stimmen nicht überein');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      setSuccess(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="hero-section">
        <div className="form-container text-center">
          <div className="glass-card p-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#C53030]/10 flex items-center justify-center">
              <svg className="w-8 h-8 text-[#C53030]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </div>
            <h2 className="font-heading text-2xl font-bold text-[#C53030] mb-2">Ungültiger Link</h2>
            <p className="font-mono text-sm text-[#6B5B4F] mb-4">Dieser Link ist ungültig.</p>
            <button onClick={() => navigate('/forgot-password')} className="mystery-button">Neuen Link anfordern</button>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="hero-section">
        <div className="form-container text-center">
          <div className="glass-card p-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#2F855A]/10 flex items-center justify-center">
              <svg className="w-8 h-8 text-[#2F855A]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
            </div>
            <h2 className="font-heading text-2xl font-bold text-[#2F855A] mb-2">Passwort geändert!</h2>
            <p className="font-mono text-sm text-[#6B5B4F] mb-6">Du kannst dich jetzt mit deinem neuen Passwort anmelden.</p>
            <button onClick={() => navigate('/login')} className="mystery-button" data-testid="go-to-login">Zur Anmeldung</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="hero-section">
      <div className="form-container">
        <div className="text-center mb-8 opacity-0 animate-fadeInUp stagger-1">
          <p className="font-mono text-xs tracking-[0.3em] text-[#6B5B4F] mb-4">NEUES PASSWORT</p>
          <h1 className="font-heading text-4xl md:text-5xl font-black uppercase tracking-tighter text-[#2D2016]">Zurücksetzen</h1>
        </div>
        <form onSubmit={handleSubmit} className="space-y-6 opacity-0 animate-fadeInUp stagger-2" data-testid="reset-password-form">
          <div>
            <label className="font-mono text-xs text-[#6B5B4F] uppercase tracking-widest mb-2 block">Neues Passwort</label>
            <input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="mystery-input w-full" placeholder="Mindestens 6 Zeichen" data-testid="reset-password" />
          </div>
          <div>
            <label className="font-mono text-xs text-[#6B5B4F] uppercase tracking-widest mb-2 block">Passwort bestätigen</label>
            <input type="password" required minLength={6} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="mystery-input w-full" placeholder="Passwort wiederholen" data-testid="reset-confirm-password" />
          </div>
          {error && <div className="bg-[#C53030]/10 border border-[#C53030] p-4 text-center"><p className="text-[#C53030] font-mono text-sm font-bold">{error}</p></div>}
          <button type="submit" disabled={loading} className="mystery-button w-full" data-testid="reset-submit">{loading ? 'Wird gespeichert...' : 'Passwort ändern'}</button>
        </form>
      </div>
    </div>
  );
};

// Guess Page
const GuessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, refreshUser, logout } = useAuth();
  const [answer, setAnswer] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [stats, setStats] = useState(null);
  const source = searchParams.get('src') || searchParams.get('source') || null;

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/stats`);
        setStats(response.data);
      } catch (err) {
        // Stats are non-critical, silently ignore
      }
    };
    fetchStats();
  }, []);

  if (user?.has_participated) {
    return (
      <div className="result-container">
        {user.is_winner ? (
          <>
            <div className="winner-glow"></div>
            <div className="text-center relative z-10 max-w-lg px-4">
              <p className="font-mono text-xs tracking-[0.3em] text-[#2F855A] mb-4">GEWINNER</p>
              <h1 className="font-heading text-4xl md:text-5xl font-black uppercase tracking-tighter text-[#2D2016]">Du hast<br />gewonnen!</h1>
              <p className="font-mono text-sm text-[#6B5B4F] mt-6 mb-8">Hier ist dein Gutschein:</p>
              <div className="coupon-code inline-block" data-testid="coupon-code">{user.coupon_code}</div>
              <div className="mt-8">
                <p className="font-mono text-xs text-[#6B5B4F] mb-4">DEIN QR-CODE</p>
                <div className="inline-block bg-white p-4 border border-[#8B7355]/20"><img src={`${API}/coupon/${user.coupon_code}/qr?frontend_url=${encodeURIComponent(window.location.origin)}`} alt="QR Code" className="w-40 h-40" data-testid="coupon-qr-code" /></div>
              </div>
              <div className="mt-8">
                <p className="font-mono text-lg text-[#8B7355] font-bold">BUY ONE GET ONE FREE</p>
                <p className="font-mono text-xs text-[#6B5B4F] mt-2">Zeige diesen QR-Code bei deinem Besuch</p>
                <p className="font-mono text-xs text-[#6B5B4F]/70 mt-4">Der QR-Code wurde dir auch per E-Mail zugeschickt.<br />Bitte schaue auch in deinem Spam-Ordner nach!</p>
              </div>
              <button onClick={logout} className="mt-12 font-mono text-xs text-[#6B5B4F] hover:text-[#8B7355] transition-colors underline">Abmelden</button>
            </div>
          </>
        ) : user.is_correct ? (
          <>
            <div className="loser-glow"></div>
            <div className="text-center relative z-10 max-w-lg px-4">
              <p className="font-mono text-xs tracking-[0.3em] text-[#8B7355] mb-4">RICHTIG GERATEN</p>
              <h1 className="font-heading text-4xl md:text-5xl font-black uppercase tracking-tighter text-[#2D2016]">Leider zu<br />spät!</h1>
              <p className="font-mono text-sm text-[#6B5B4F] mt-6 mb-8">Du hast richtig geraten! Leider sind alle 50 Gutscheine bereits vergeben.<br />Wir freuen uns trotzdem auf deinen Besuch!</p>
              <Countdown />
              <button onClick={logout} className="mt-12 font-mono text-xs text-[#6B5B4F] hover:text-[#8B7355] transition-colors underline">Abmelden</button>
            </div>
          </>
        ) : (
          <>
            <div className="loser-glow"></div>
            <div className="text-center relative z-10 max-w-lg px-4">
              <p className="font-mono text-xs tracking-[0.3em] text-[#6B5B4F] mb-4">SCHADE</p>
              <h1 className="font-heading text-4xl md:text-5xl font-black uppercase tracking-tighter text-[#2D2016]">Leider<br />falsch</h1>
              <p className="font-mono text-sm text-[#6B5B4F] mt-6 mb-8">Das war leider nicht die richtige Antwort.<br />Aber wir freuen uns auf deinen Besuch!</p>
              <Countdown />
              <button onClick={logout} className="mt-12 font-mono text-xs text-[#6B5B4F] hover:text-[#8B7355] transition-colors underline">Abmelden</button>
            </div>
          </>
        )}
      </div>
    );
  }

  // couponsGone check removed - users can still guess even when all coupons are taken

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/participate`, { answer, source });
      await refreshUser();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="hero-section">
      <div className="question-mark">?</div>
      <div className="form-container">
        <div className="text-center mb-8">
          <p className="font-mono text-xs text-[#6B5B4F] mb-2">Hallo, {user?.name}!</p>
          <h1 className="font-heading text-4xl md:text-5xl font-black uppercase tracking-tighter text-[#2D2016]">Was kommt<br />hierhin?</h1>
        </div>
        <div className="flex justify-center mb-8"><Countdown /></div>
        <form onSubmit={handleSubmit} className="space-y-6" data-testid="guess-form">
          <div>
            <label className="font-mono text-xs text-[#6B5B4F] uppercase tracking-widest mb-4 block text-center">Wähle deine Antwort</label>
            <div className="grid grid-cols-2 gap-3">
              {['Café', 'Shisha-Bar', 'Fitness- & Trainingsbedarf', 'Florist'].map((option) => (
                <button key={option} type="button" onClick={() => setAnswer(option)}
                  className={`p-4 font-mono text-sm uppercase tracking-wider border transition-all ${answer === option ? 'bg-[#8B7355] text-white border-[#8B7355]' : 'bg-white text-[#2D2016] border-[#8B7355]/20 hover:border-[#8B7355]/50'}`}
                  data-testid={`option-${option.toLowerCase().replace(' ', '-')}`}>{option}</button>
              ))}
            </div>
          </div>
          {error && <div className="bg-[#C53030]/10 border border-[#C53030] p-4 text-center"><p className="text-[#C53030] font-mono text-sm font-bold">{error}</p></div>}
          <button type="submit" disabled={loading || !answer} className="mystery-button w-full" data-testid="submit-button">{loading ? 'Wird überprüft...' : 'Jetzt raten'}</button>
        </form>
        <p className="font-mono text-[10px] text-[#6B5B4F]/50 text-center mt-8">Du hast nur einen Versuch!</p>
        <button onClick={logout} className="w-full mt-4 font-mono text-xs text-[#6B5B4F] hover:text-[#8B7355] transition-colors">Abmelden</button>
      </div>
    </div>
  );
};

// Redeem Page
const RedeemPage = () => {
  const { couponCode } = useParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(false);
  const [credentials, setCredentials] = useState({ username: 'admin', password: '' });
  const [showAuth, setShowAuth] = useState(false);
  const [authError, setAuthError] = useState('');

  const fetchStatus = useCallback(async () => {
    try { const r = await axios.get(`${API}/coupon/${couponCode}/status`); setStatus(r.data); }
    catch (e) { setStatus({ is_valid: false, message: 'Coupon nicht gefunden' }); }
    finally { setLoading(false); }
  }, [couponCode]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleRedeem = async () => {
    setAuthError(''); setRedeeming(true);
    try {
      const r = await axios.post(`${API}/coupon/${couponCode}/redeem`, {}, { auth: { username: credentials.username, password: credentials.password } });
      setStatus(r.data); setShowAuth(false);
    } catch (e) { setAuthError(e.response?.status === 401 ? 'Ungültige Anmeldedaten' : formatApiError(e)); }
    finally { setRedeeming(false); }
  };

  if (loading) return <div className="min-h-screen bg-[#FBF9F6] flex items-center justify-center"><p className="font-mono text-[#6B5B4F]">Wird geladen...</p></div>;

  return (
    <div className="min-h-screen bg-[#FBF9F6] flex items-center justify-center p-4">
      <div className="w-full max-w-md text-center">
        <div className="mb-8">
          <p className="font-mono text-xs tracking-[0.3em] text-[#6B5B4F] mb-4">COUPON</p>
          <div className="coupon-code inline-block text-lg">{couponCode}</div>
        </div>
        {(() => {
          if (!status?.is_valid) {
            return (
              <div className="glass-card p-8">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#C53030]/10 flex items-center justify-center"><svg className="w-8 h-8 text-[#C53030]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></div>
                <h2 className="font-heading text-2xl font-bold text-[#C53030] mb-2">UNGÜLTIG</h2>
                <p className="font-mono text-sm text-[#6B5B4F]">{status?.message}</p>
              </div>
            );
          }
          if (status?.is_redeemed) {
            return (
              <div className="glass-card p-8">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#6B5B4F]/10 flex items-center justify-center"><svg className="w-8 h-8 text-[#6B5B4F]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg></div>
                <h2 className="font-heading text-2xl font-bold text-[#6B5B4F] mb-2">EINGELÖST</h2>
                <p className="font-mono text-sm text-[#6B5B4F] mb-4">{status?.message}</p>
                <div className="text-left space-y-2 mt-6 pt-6 border-t border-[#8B7355]/20">
                  <p className="font-mono text-xs text-[#6B5B4F]">Name: <span className="text-[#2D2016]">{status?.winner_name}</span></p>
                  <p className="font-mono text-xs text-[#6B5B4F]">Eingelöst: <span className="text-[#2D2016]">{new Date(status?.redeemed_at).toLocaleString('de-DE')}</span></p>
                </div>
              </div>
            );
          }
          return (
            <div className="glass-card p-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#2F855A]/10 flex items-center justify-center"><svg className="w-8 h-8 text-[#2F855A]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg></div>
              <h2 className="font-heading text-2xl font-bold text-[#2F855A] mb-2">GÜLTIG</h2>
              <p className="font-mono text-sm text-[#6B5B4F] mb-4">{status?.message}</p>
              <div className="text-left space-y-2 mb-6 pb-6 border-b border-[#8B7355]/20">
                <p className="font-mono text-xs text-[#6B5B4F]">Name: <span className="text-[#2D2016]">{status?.winner_name}</span></p>
                <p className="font-mono text-xs text-[#6B5B4F]">E-Mail: <span className="text-[#2D2016]">{status?.winner_email}</span></p>
              </div>
              <div className="font-mono text-lg text-[#8B7355] font-bold mb-6">BUY ONE GET ONE FREE</div>
              {!showAuth ? (
                <button onClick={() => setShowAuth(true)} className="mystery-button w-full" data-testid="redeem-button">Jetzt einlösen</button>
              ) : (
                <div className="space-y-4">
                  <input type="password" placeholder="Admin Passwort" value={credentials.password} onChange={(e) => setCredentials({...credentials, password: e.target.value})} className="mystery-input w-full text-center" />
                  {authError && <p className="text-[#C53030] font-mono text-xs">{authError}</p>}
                  <div className="flex gap-2">
                    <button onClick={() => setShowAuth(false)} className="flex-1 font-mono text-xs text-[#6B5B4F] border border-[#8B7355]/20 py-3 hover:bg-[#8B7355]/5">Abbrechen</button>
                    <button onClick={handleRedeem} disabled={redeeming || !credentials.password} className="mystery-button flex-1">{redeeming ? 'Wird eingelöst...' : 'Bestätigen'}</button>
                  </div>
                </div>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
};

// Scanner Page
const ScannerPage = () => {
  const navigate = useNavigate();
  const [manualCode, setManualCode] = useState('');
  const [credentials, setCredentials] = useState({ username: 'admin', password: '' });
  const [isAuth, setIsAuth] = useState(false);
  const [authError, setAuthError] = useState('');

  const handleAuth = async (e) => {
    e.preventDefault(); setAuthError('');
    try { await axios.get(`${API}/admin/stats`, { auth: { username: credentials.username, password: credentials.password } }); sessionStorage.setItem('scannerAuth', btoa(`${credentials.username}:${credentials.password}`)); setIsAuth(true); }
    catch (e) { setAuthError('Ungültige Anmeldedaten'); }
  };

  const handleManualSubmit = (e) => { e.preventDefault(); if (manualCode.trim()) navigate(`/redeem/${manualCode.trim().toUpperCase()}`); };

  useEffect(() => { if (sessionStorage.getItem('scannerAuth')) setIsAuth(true); }, []);

  if (!isAuth) return (
    <div className="min-h-screen bg-[#FBF9F6] flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8"><p className="font-mono text-xs tracking-[0.3em] text-[#6B5B4F] mb-4">STAFF SCANNER</p><h1 className="font-heading text-3xl font-black uppercase tracking-tighter text-[#2D2016]">Anmelden</h1></div>
        <form onSubmit={handleAuth} className="space-y-6">
          <div><label className="font-mono text-xs text-[#6B5B4F] uppercase tracking-widest mb-2 block">Passwort</label><input type="password" required value={credentials.password} onChange={(e) => setCredentials({...credentials, password: e.target.value})} className="mystery-input w-full" /></div>
          {authError && <p className="text-[#C53030] font-mono text-sm">{authError}</p>}
          <button type="submit" className="mystery-button w-full">Anmelden</button>
        </form>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FBF9F6] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8"><p className="font-mono text-xs tracking-[0.3em] text-[#6B5B4F] mb-4">STAFF SCANNER</p><h1 className="font-heading text-3xl font-black uppercase tracking-tighter text-[#2D2016]">Coupon Scanner</h1></div>
        <div className="glass-card p-8">
          <div className="text-center mb-8">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full border-2 border-dashed border-[#8B7355] flex items-center justify-center"><svg className="w-10 h-10 text-[#8B7355]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" /></svg></div>
            <p className="font-mono text-sm text-[#6B5B4F]">Scanne den QR-Code oder gib den Code manuell ein</p>
          </div>
          <form onSubmit={handleManualSubmit} className="space-y-4">
            <input type="text" value={manualCode} onChange={(e) => setManualCode(e.target.value.toUpperCase())} className="mystery-input w-full text-center text-lg tracking-widest" placeholder="BOGO-XXXXXXXX" />
            <button type="submit" disabled={!manualCode.trim()} className="mystery-button w-full">Code prüfen</button>
          </form>
          <div className="mt-8 pt-6 border-t border-[#8B7355]/20 text-center"><button onClick={() => navigate('/admin/dashboard')} className="font-mono text-xs text-[#6B5B4F] hover:text-[#8B7355]">Zum Admin Dashboard →</button></div>
        </div>
      </div>
    </div>
  );
};

// Admin Login
const AdminLogin = () => {
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState({ username: 'admin', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    try { await axios.get(`${API}/admin/stats`, { auth: { username: credentials.username, password: credentials.password } }); sessionStorage.setItem('adminAuth', btoa(`${credentials.username}:${credentials.password}`)); navigate('/admin/dashboard'); }
    catch (e) { setError('Ungültige Anmeldedaten'); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#FBF9F6] flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8"><p className="font-mono text-xs tracking-[0.3em] text-[#6B5B4F] mb-4">ADMIN</p><h1 className="font-heading text-3xl font-black uppercase tracking-tighter text-[#2D2016]">Control Room</h1></div>
        <form onSubmit={handleLogin} className="space-y-6">
          <div><label className="font-mono text-xs text-[#6B5B4F] uppercase tracking-widest mb-2 block">Benutzername</label><input type="text" required value={credentials.username} onChange={(e) => setCredentials({...credentials, username: e.target.value})} className="mystery-input w-full" /></div>
          <div><label className="font-mono text-xs text-[#6B5B4F] uppercase tracking-widest mb-2 block">Passwort</label><input type="password" required value={credentials.password} onChange={(e) => setCredentials({...credentials, password: e.target.value})} className="mystery-input w-full" /></div>
          {error && <p className="text-[#C53030] font-mono text-sm">{error}</p>}
          <button type="submit" disabled={loading} className="mystery-button w-full">{loading ? 'Wird geladen...' : 'Anmelden'}</button>
        </form>
        <div className="mt-6 text-center"><button onClick={() => navigate('/scanner')} className="font-mono text-xs text-[#6B5B4F] hover:text-[#8B7355]">Zum Coupon Scanner →</button></div>
      </div>
    </div>
  );
};

// Admin Dashboard
const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [winners, setWinners] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  const getAuthHeader = useCallback(() => { 
    const a = sessionStorage.getItem('adminAuth'); 
    if (!a) { navigate('/admin'); return null; } 
    return { Authorization: `Basic ${a}` }; 
  }, [navigate]);

  const fetchData = useCallback(async () => {
    const h = getAuthHeader(); if (!h) return;
    try {
      const [s, p, w] = await Promise.all([axios.get(`${API}/admin/stats`, { headers: h }), axios.get(`${API}/admin/participants`, { headers: h }), axios.get(`${API}/admin/winners`, { headers: h })]);
      setStats(s.data); setParticipants(p.data); setWinners(w.data);
    } catch (e) { if (e.response?.status === HTTP_UNAUTHORIZED) { sessionStorage.removeItem('adminAuth'); navigate('/admin'); } }
    finally { setLoading(false); }
  }, [getAuthHeader, navigate]);

  useEffect(() => { fetchData(); const interval = setInterval(fetchData, REFRESH_INTERVAL); return () => clearInterval(interval); }, [fetchData]);

  const handleLogout = () => { sessionStorage.removeItem('adminAuth'); navigate('/admin'); };
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailResult, setEmailResult] = useState(null);

  const handleSendOpeningEmail = async () => {
    if (!window.confirm('Eröffnungs-E-Mail an ALLE verifizierten Teilnehmer senden? Diese Aktion kann nicht rückgängig gemacht werden.')) return;
    setSendingEmail(true);
    setEmailResult(null);
    try {
      const response = await axios.post(`${API}/admin/send-opening-email`, {}, { headers: getAuthHeader() });
      setEmailResult(response.data);
    } catch (e) {
      setEmailResult({ message: 'Fehler beim Senden: ' + (e.response?.data?.detail || e.message), failed: true });
    } finally {
      setSendingEmail(false);
    }
  };

  if (loading) return <div className="min-h-screen bg-[#FBF9F6] flex items-center justify-center"><p className="font-mono text-[#6B5B4F]">Wird geladen...</p></div>;

  return (
    <div className="admin-container">
      <div className="max-w-7xl mx-auto">
        <div className="admin-header">
          <div><p className="font-mono text-xs tracking-[0.3em] text-[#6B5B4F]">ADMIN</p><h1 className="font-heading text-2xl font-black uppercase tracking-tighter text-[#2D2016]">Control Room</h1></div>
          <div className="flex gap-4 items-center">
            <button onClick={handleSendOpeningEmail} disabled={sendingEmail} className="font-mono text-xs text-white bg-[#2F855A] px-4 py-2 hover:bg-[#276749] transition-colors disabled:opacity-50" data-testid="send-opening-email">{sendingEmail ? 'Wird gesendet...' : 'Eröffnungs-E-Mail senden'}</button>
            <button onClick={() => navigate('/scanner')} className="font-mono text-xs text-[#8B7355] border border-[#8B7355] px-4 py-2 hover:bg-[#8B7355] hover:text-white transition-colors">Scanner</button>
            <button onClick={handleLogout} className="font-mono text-xs text-[#6B5B4F] hover:text-[#8B7355]">Abmelden</button>
          </div>
        </div>
        {emailResult && (
          <div className={`mb-4 p-4 border font-mono text-sm ${emailResult.failed ? 'bg-[#C53030]/10 border-[#C53030] text-[#C53030]' : 'bg-[#2F855A]/10 border-[#2F855A] text-[#2F855A]'}`} data-testid="email-result">
            {emailResult.message}
            {emailResult.sent > 0 && <span> ({emailResult.sent}/{emailResult.total} erfolgreich)</span>}
            <button onClick={() => setEmailResult(null)} className="ml-4 underline text-xs">Schließen</button>
          </div>
        )}
        {stats && (
          <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div className="stat-card"><div className="stat-value">{stats.total_visits || 0}</div><div className="stat-label">Seitenaufrufe</div></div>
            <div className="stat-card"><div className="stat-value">{stats.today_visits || 0}</div><div className="stat-label">Heute</div></div>
            <div className="stat-card"><div className="stat-value">{stats.total_registered || 0}</div><div className="stat-label">Registriert</div></div>
            <div className="stat-card"><div className="stat-value">{stats.total_verified || 0}</div><div className="stat-label">Verifiziert</div></div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="stat-card"><div className="stat-value">{stats.total_participants}</div><div className="stat-label">Teilgenommen</div></div>
            <div className="stat-card"><div className="stat-value">{stats.total_winners}</div><div className="stat-label">Gewinner</div></div>
            <div className="stat-card"><div className="stat-value">{stats.winners_remaining}</div><div className="stat-label">Verbleibend</div></div>
            <div className="stat-card"><div className="stat-value">{stats.coupons_redeemed || 0}</div><div className="stat-label">Eingelöst</div></div>
          </div>
          </>
        )}
        {stats?.sources && Object.keys(stats.sources).length > 0 && (
          <div className="mb-8">
            <h2 className="font-mono text-xs text-[#6B5B4F] uppercase tracking-widest mb-4">Quellen</h2>
            <div className="flex flex-wrap gap-2">{Object.entries(stats.sources).map(([s, c]) => (<div key={s} className="glass-card px-4 py-2"><span className="font-mono text-sm text-[#8B7355]">{s}</span><span className="font-mono text-sm text-[#6B5B4F] ml-2">({c})</span></div>))}</div>
          </div>
        )}
        <div className="flex gap-4 mb-6 border-b border-[#8B7355]/20 pb-4">
          {[{ id: 'overview', label: 'Teilnehmer' }, { id: 'winners', label: 'Gewinner' }, { id: 'coupons', label: 'Coupons' }].map((t) => (
            <button key={t.id} onClick={() => setActiveTab(t.id)} className={`font-mono text-xs uppercase tracking-wider ${activeTab === t.id ? 'text-[#8B7355]' : 'text-[#6B5B4F] hover:text-[#8B7355]'}`}>{t.label}</button>
          ))}
        </div>
        <div className="overflow-x-auto">
          {activeTab === 'overview' && (
            <table className="admin-table"><thead><tr><th>Name</th><th>E-Mail</th><th>Antwort</th><th>Quelle</th><th>Status</th><th>Datum</th></tr></thead>
              <tbody>{participants.map((p) => (<tr key={p.id}><td>{p.name}</td><td>{p.email}</td><td>{p.answer}</td><td>{p.source || '-'}</td><td><span className={p.is_winner ? 'badge-winner' : 'badge-loser'}>{p.is_winner ? 'Gewinner' : 'Teilnehmer'}</span></td><td>{formatDate(p.created_at)}</td></tr>))}
                {participants.length === 0 && <tr><td colSpan="6" className="text-center text-[#6B5B4F] py-8">Noch keine Teilnehmer</td></tr>}</tbody>
            </table>
          )}
          {activeTab === 'winners' && (
            <table className="admin-table"><thead><tr><th>Name</th><th>E-Mail</th><th>Coupon</th><th>Status</th><th>Datum</th></tr></thead>
              <tbody>{winners.map((w) => (<tr key={w.id}><td>{w.name}</td><td>{w.email}</td><td className="text-[#8B7355] font-bold">{w.coupon_code}</td><td>{w.coupon_redeemed ? <span className="text-[#6B5B4F] font-mono text-xs">Eingelöst</span> : <span className="text-[#2F855A] font-mono text-xs">Offen</span>}</td><td>{formatDate(w.created_at)}</td></tr>))}
                {winners.length === 0 && <tr><td colSpan="5" className="text-center text-[#6B5B4F] py-8">Noch keine Gewinner</td></tr>}</tbody>
            </table>
          )}
          {activeTab === 'coupons' && (
            <table className="admin-table"><thead><tr><th>Coupon</th><th>QR</th><th>Name</th><th>Status</th><th>Aktion</th></tr></thead>
              <tbody>{winners.map((w) => (<tr key={w.id}><td className="text-[#8B7355] font-bold text-lg">{w.coupon_code}</td><td><img src={`${API}/coupon/${w.coupon_code}/qr?frontend_url=${encodeURIComponent(window.location.origin)}`} alt="QR" className="w-12 h-12 bg-white p-1 border border-[#8B7355]/20" /></td><td>{w.name}</td><td>{w.coupon_redeemed ? <span className="badge-loser">Eingelöst</span> : <span className="badge-winner">Gültig</span>}</td><td><button onClick={() => navigate(`/redeem/${w.coupon_code}`)} className="font-mono text-xs text-[#8B7355] hover:underline">Öffnen</button></td></tr>))}
                {winners.length === 0 && <tr><td colSpan="5" className="text-center text-[#6B5B4F] py-8">Noch keine Coupons</td></tr>}</tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/verify" element={<VerifyEmailPage />} />
            <Route path="/guess" element={<ProtectedRoute><GuessPage /></ProtectedRoute>} />
            <Route path="/redeem/:couponCode" element={<RedeemPage />} />
            <Route path="/scanner" element={<ScannerPage />} />
            <Route path="/admin" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
